import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Spinner, Alert } from '@material-tailwind/react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboard, faBoxes, faDollarSign, faNoteSticky, faArrowRightArrowLeft, faEye, faDownload, faPrint } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import JobCardOverview from './tabs/JobCardOverview';
import JobCardPartsList from './tabs/JobCardPartsList';
import JobCardChargesTab from './tabs/JobCardChargesTab';
import JobCardNotes from './tabs/JobCardNotes';
import ChargeSummaryCard from './components/ChargeSummaryCard';
import MoveJobCardDialog from './MoveJobCardDialog';
import JobCardDocumentPreviewDialog from './JobCardDocumentPreviewDialog';
import { fetchPrintableJobCard } from './jobCardDocumentService';
import { downloadJobCardPDF, printJobCardPDF } from './jobCardPDFGenerator';

export default function JobCardPage() {
    const { jobCardId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const generatedBy = user?.name || user?.username || localStorage.getItem('cfms-name') || localStorage.getItem('username') || 'Fleet Management';
    const [jobCard, setJobCard] = useState(null);
    const [charges, setCharges] = useState([]);
    const [chargeTypes, setChargeTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [permissions, setPermissions] = useState([]);
    const [showMoveDialog, setShowMoveDialog] = useState(false);
    const [showDocumentPreview, setShowDocumentPreview] = useState(false);
    const [documentBusy, setDocumentBusy] = useState('');
    const [documentError, setDocumentError] = useState('');

    useEffect(() => {
        const token = localStorage.getItem('authToken');
        if (token) {
            axios.get(`${rootUrl}/api/users/me/permissions`, { headers: { Authorization: `Bearer ${token}` } })
                .then(res => setPermissions(res.data?.data || res.data || []))
                .catch(() => setPermissions([]));
        }
    }, []);

    const loadJobCard = useCallback(async () => {
        try {
            setError(null);

            const [jobCardRes, chargesRes, typesRes] = await Promise.all([
                axios.get(`${rootUrl}/api/job-cards/${jobCardId}`, {
                    headers: { Authorization: `Bearer ${user?.token}` }
                }),
                axios.get(`${rootUrl}/api/job-cards/${jobCardId}/charges`, {
                    headers: { Authorization: `Bearer ${user?.token}` }
                }),
                axios.get(`${rootUrl}/api/job-card-charge-types/active`, {
                    headers: { Authorization: `Bearer ${user?.token}` }
                })
            ]);

            setJobCard(jobCardRes.data?.data || jobCardRes.data);
            setCharges(chargesRes.data?.data || []);
            setChargeTypes(typesRes.data?.data || []);
        } catch (err) {
            console.error('Error loading job card:', err);
            setError(err.response?.data?.message || 'Failed to load job card');
        } finally {
            setLoading(false);
        }
    }, [jobCardId, user?.token]);

    useEffect(() => {
        loadJobCard();
    }, [jobCardId, loadJobCard]);

    const loadPrintableJobCard = async () => {
        const token = localStorage.getItem('authToken') || user?.token;
        return fetchPrintableJobCard(token, jobCardId);
    };

    const handleDownloadDocument = async () => {
        try {
            setDocumentBusy('download');
            setDocumentError('');
            const payload = await loadPrintableJobCard();
            await downloadJobCardPDF(payload.jobCard, payload.companyInfo, generatedBy);
        } catch (err) {
            console.error('Error downloading job card document:', err);
            setDocumentError(err.response?.data?.message || err.message || 'Failed to download PDF');
        } finally {
            setDocumentBusy('');
        }
    };

    const handlePrintDocument = async () => {
        try {
            setDocumentBusy('print');
            setDocumentError('');
            const payload = await loadPrintableJobCard();
            await printJobCardPDF(payload.jobCard, payload.companyInfo, generatedBy);
        } catch (err) {
            console.error('Error printing job card document:', err);
            setDocumentError(err.response?.data?.message || err.message || 'Failed to print PDF');
        } finally {
            setDocumentBusy('');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Spinner className="h-12 w-12" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <Alert color="red" variant="outlined" className="mb-4">
                    {error}
                </Alert>
                <Button onClick={() => navigate(-1)}>Go Back</Button>
            </div>
        );
    }

    if (!jobCard) {
        return (
            <div className="p-6">
                <Alert color="red" variant="outlined">
                    Job card not found
                </Alert>
                <Button onClick={() => navigate(-1)}>Go Back</Button>
            </div>
        );
    }

    const tabs = [
        { label: 'Overview', value: 'overview', icon: faClipboard },
        { label: 'Parts', value: 'parts', icon: faBoxes },
        { label: 'Labor & Charges', value: 'charges', icon: faDollarSign },
        { label: 'Notes', value: 'notes', icon: faNoteSticky }
    ];

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
                <Button
                    variant="text"
                    className="text-blue-600 disabled:text-gray-400"
                    onClick={() => navigate(-1)}
                >
                    ← Back
                </Button>
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        size="sm"
                        variant="outlined"
                        className="flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-700 rounded-sm shadow-none border-slate-300"
                        onClick={() => setShowDocumentPreview(true)}
                    >
                        <FontAwesomeIcon icon={faEye} />
                        Preview
                    </Button>
                    <Button
                        size="sm"
                        variant="outlined"
                        className="flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-700 rounded-sm shadow-none border-slate-300"
                        onClick={handleDownloadDocument}
                        disabled={documentBusy === 'download'}
                    >
                        <FontAwesomeIcon icon={faDownload} />
                        {documentBusy === 'download' ? 'Downloading...' : 'Download PDF'}
                    </Button>
                    <Button
                        size="sm"
                        variant="outlined"
                        className="flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-700 rounded-sm shadow-none border-slate-300"
                        onClick={handlePrintDocument}
                        disabled={documentBusy === 'print'}
                    >
                        <FontAwesomeIcon icon={faPrint} />
                        {documentBusy === 'print' ? 'Printing...' : 'Print'}
                    </Button>
                    {permissions.includes('job_card.move') && !jobCard?.cancelled && (
                        <Button
                            size="sm"
                            variant="outlined"
                            className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-sm shadow-none border-slate-300"
                            onClick={() => setShowMoveDialog(true)}
                        >
                            <FontAwesomeIcon icon={faArrowRightArrowLeft} />
                            Move to Owner
                        </Button>
                    )}
                </div>
            </div>

            {documentError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {documentError}
                </div>
            )}

            <MoveJobCardDialog
                open={showMoveDialog}
                onClose={() => setShowMoveDialog(false)}
                jobCardId={jobCardId}
                jobCardNo={jobCard?.job_card_no}
                currentOwnerCode={jobCard?.owner_code}
                currentOwnerName={jobCard?.owner_name}
                onSuccess={loadJobCard}
            />

            <JobCardDocumentPreviewDialog
                open={showDocumentPreview}
                onClose={() => setShowDocumentPreview(false)}
                jobCardId={jobCardId}
            />

            {/* Summary Card */}
            <ChargeSummaryCard jobCard={jobCard} charges={charges} onStatusUpdate={loadJobCard} />

            {/* Tabs Card */}
            <div className="bg-white rounded-2xl shadow-xl p-6 mt-6">
                {/* Tab Headers */}
                <div className="flex border-b border-gray-200 mb-6">
                    {tabs.map(({ label, value, icon }) => (
                        <button
                            key={value}
                            onClick={() => setActiveTab(value)}
                            aria-label={label}
                            className={`px-6 py-3 font-semibold text-base transition-all duration-200 border-b-2 ${activeTab === value
                                ? 'border-[#FF5733] text-[#FF5733]'
                                : 'border-transparent text-gray-600 hover:text-gray-900'
                                }`}
                        >
                            <span className="md:hidden inline-block">
                                <FontAwesomeIcon icon={icon} className="w-5 h-5" />
                            </span>
                            <span className="hidden md:inline">{label}</span>
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                {activeTab === 'overview' && (
                    <JobCardOverview
                        jobCard={jobCard}
                        onBack={() => navigate(-1)}
                        onRefresh={loadJobCard}
                        permissions={permissions}
                    />
                )}
                {activeTab === 'parts' && (
                    <JobCardPartsList jobCardId={jobCardId} jobCard={jobCard} locked={jobCard?.locked} onRefresh={loadJobCard} />
                )}
                {activeTab === 'charges' && (
                    <JobCardChargesTab
                        jobCardId={jobCardId}
                        charges={charges}
                        chargeTypes={chargeTypes}
                        locked={jobCard?.locked}
                        onChargeAdded={() => loadJobCard()}
                        onChargeUpdated={() => loadJobCard()}
                        onChargeRemoved={() => loadJobCard()}
                    />
                )}
                {activeTab === 'notes' && (
                    <JobCardNotes jobCard={jobCard} onUpdate={loadJobCard} />
                )}
            </div>
        </div>
    );
}
